import React from "react";

import { PaymentMethodCard } from "@/components/billing/payment-method-card";

import { cx } from "@/lib/tailwind-utils";

import { PaymentMedium } from "@/types/enums/payment";
import { MerchantAccount } from "@/types/merchant-account";
import { PaymentMethod } from "@/types/payment";

interface SavedPaymentMethodsProps {
  paymentMethods: PaymentMethod[];
  selectedPaymentMethod?: PaymentMethod | null;
  setSelectedPaymentMethod: (paymentMethod: PaymentMethod | null) => void;
  setPaymentMedium?: (paymentMedium: PaymentMedium) => void;
  className?: string;
  merchantAccount?: MerchantAccount | null;
  merchantAccounts?: MerchantAccount[];
  /** Optional per-card action slot (e.g. a remove menu). Not rendered on the default card. */
  renderActions?: (paymentMethod: PaymentMethod) => React.ReactNode;
}

const SavedPaymentMethods: React.FC<SavedPaymentMethodsProps> = ({
  paymentMethods,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  setPaymentMedium,
  className,
  merchantAccount,
  merchantAccounts = [],
  renderActions,
}) => {
  const isPaymentMethodAvailable = (method: PaymentMethod): boolean => {
    if (!merchantAccount) return true;
    // availableMerchantIds: null = all merchants; [] = none; [...] = restricted to listed merchants.
    if (
      method.availableMerchantIds === null ||
      method.availableMerchantIds === undefined
    )
      return true;
    if (method.availableMerchantIds.length === 0) return false;
    return method.availableMerchantIds.includes(merchantAccount.id);
  };

  const handleSelectPaymentMethod = (paymentMethod: PaymentMethod) => {
    if (!isPaymentMethodAvailable(paymentMethod)) return;

    if (paymentMethod.id === selectedPaymentMethod?.id) {
      setSelectedPaymentMethod(null);
      setPaymentMedium?.(PaymentMedium.VIRTUAL_TERMINAL);
    } else {
      setSelectedPaymentMethod(paymentMethod);
      setPaymentMedium?.(PaymentMedium.SAVED_PAYMENT_METHOD);
    }
  };

  return (
    <div className={cx("sm:max-w-3xl md:col-span-2", className)}>
      <div className="flex flex-wrap justify-between gap-3 md:justify-start">
        {paymentMethods.map((method) => (
          <PaymentMethodCard
            key={method.id}
            method={method}
            merchantAccounts={merchantAccounts}
            isSelectable
            isSelected={selectedPaymentMethod?.id === method.id}
            isAvailable={isPaymentMethodAvailable(method)}
            onSelect={() => handleSelectPaymentMethod(method)}
            actions={renderActions?.(method)}
          />
        ))}
      </div>
    </div>
  );
};

export default SavedPaymentMethods;
